// stores/SmartBannerStore.ts
import { action, makeObservable, observable, reaction } from "mobx";
import { ActiveDomain, AcxStore } from "./RootStore";
import type { IRootStore } from "stores/RootStore";
import * as ApplicationFiltersStore from "./ApplicationFilters/ApplicationFiltersStore";
import { BaseStore } from "./BaseStore";
import React from "react";
import { buildSmartBannerBlueprint } from "components/SmartBanner/BannerFacilitator";
import { buildSmartBanner } from "components/SmartBanner/SmartBannerBuilder";

// Smart Banner Context is capturing "transition" state of filters
// e.g. I click to view in conversations, filters are automatically applied
// I click to view a conversation, filters may have previously been applied
// In both examples, we want to capture for smart banner what filters were applied
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import { AcxTranscriptionStore } from "components/UI/AcxTranscription/Stores/AcxTranscriptionStore";

export type ContextActions = "ViewInConversations" | "FocusConversation";
export type SmartBannerContext =
    | {
          actionTaken: "ViewInConversations";
          selectedSavedFilter: ApplicationFiltersStore.SavedFilter | undefined;
          filters: Partial<ApplicationFiltersStore.ApplicationFilters>;
          hierarchyName?: string;
          source?: "signals" | "proactiveInsights";
          proactiveInsightType?: string;
      }
    | {
          actionTaken: "FocusConversation";
          filters: Partial<ApplicationFiltersStore.ApplicationFilters>;
          conversationStore: ConversationsStore;
          transcriptionStore: AcxTranscriptionStore;
          onClick: () => void;
      };

@AcxStore
export default class ContextStore extends BaseStore {
    private attemptedToRenderBanner = false;

    @observable.ref context: SmartBannerContext | null = null;

    // Banner is the content to be rendered
    @observable.ref
    bannerContent: React.ReactNode | null = null;

    @observable
    rootStoreRef: IRootStore;
    applicationFiltersStore: ApplicationFiltersStore.ApplicationFiltersStore;

    /**
     * When a user visits a signals dashboard with a pinned filter, their filters
     * in the application filters store will be overriden. We use these
     * variables to store the state before applying the pinned filter so
     * that we can revert to that state later on if they leave a custom dashboard.
     */
    previousFilters?: ApplicationFiltersStore.ApplicationFilters;
    previousSelectedSavedFilter?: ApplicationFiltersStore.SavedFilter;

    constructor(private rootStore: IRootStore) {
        super("ContextStore");

        makeObservable(this);

        this.rootStoreRef = rootStore;
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore.ApplicationFiltersStore,
        );

        reaction(
            () => this.rootStore.activeLocation,
            async (_, previousLocation) => {
                // We are leaving a focused conversation, clear the context and banner
                if (previousLocation.domain === ActiveDomain.Conversation) {
                    this.clearContext();
                    this.clearBannerContent();
                }
            },
        );
    }

    get bannerExists() {
        return !!this.bannerContent;
    }

    @action
    setupContext(context: SmartBannerContext) {
        switch (context.actionTaken) {
            case "ViewInConversations":
                this.context = {
                    actionTaken: context.actionTaken,
                    selectedSavedFilter: context.selectedSavedFilter,
                    filters: context.filters,
                    hierarchyName: context.hierarchyName,
                    source: context.source,
                    proactiveInsightType: context.proactiveInsightType,
                };
                break;
            case "FocusConversation":
                this.context = {
                    actionTaken: context.actionTaken,
                    filters: context.filters,
                    conversationStore: context.conversationStore,
                    transcriptionStore: context.transcriptionStore,
                    onClick: context.onClick,
                };
                break;
        }

        this.clearBannerContent();
    }

    // Building the smart banner depends on the context and if we have already attempted to render the banner
    // If we have context and have not attempted to render the banner, we build use the faciiltator to build a blueprint
    // and then use the blueprint to build the content of the banner
    @action
    buildSmartBanner() {
        // If there is no context, or if we have already attempted to render the banner, do nothing
        if (!this.context || this.attemptedToRenderBanner) {
            return;
        }

        this.attemptedToRenderBanner = true;

        // Decide on what type of banner to build based on context => Blueprint
        const bannerBlueprint = buildSmartBannerBlueprint(
            this.context,
            this.rootStoreRef,
        );

        // Use the Blueprint to build the banner, set that to the observable banner
        this.bannerContent = buildSmartBanner(bannerBlueprint);

        // Clear the context after building the banner
        this.context = null;

        return this.bannerContent;
    }

    @action
    clearBannerContent() {
        this.bannerContent = null;
        this.attemptedToRenderBanner = false;
    }

    @action
    clearContext() {
        this.context = null;
    }

    setPrevious(
        prevFilters?: ApplicationFiltersStore.ApplicationFilters,
        prevSelectedSavedFilter?: ApplicationFiltersStore.SavedFilter,
    ) {
        this.previousFilters = prevFilters;
        this.previousSelectedSavedFilter = prevSelectedSavedFilter;
    }
}
